/**
 * The village economy's numbers, typed and indexed.
 *
 * `data/*.json` is produced by @empire/balance-tools from 02_Balance.xlsx and is
 * never hand-edited. This module is the only place those files are read, so a
 * change in the spreadsheet's shape breaks in one file rather than fifty — the
 * same arrangement core-sim's `data.ts` has for the battle numbers.
 *
 * It lives in `shared`, not in core-sim, because the SERVER owns the economy.
 * Buildings, production and storage never enter a battle, and core-sim has to
 * stay a dependency-free simulation of one.
 */
import buildingsData from "./data/buildings.json" with { type: "json" };
import costCurveData from "./data/costCurve.json" with { type: "json" };
import economyData from "./data/economy.json" with { type: "json" };
import fieldsData from "./data/fields.json" with { type: "json" };
import storageData from "./data/storage.json" with { type: "json" };

/** The four resources, in sheet order. Grain is also the army's upkeep. */
export const RESOURCE_KINDS = ["wood", "clay", "iron", "grain"] as const;
export type ResourceKind = (typeof RESOURCE_KINDS)[number];

/** An amount of each resource. Always whole numbers — no fractional wood. */
export type Resources = Record<ResourceKind, number>;

export interface Prerequisite {
  readonly building: string;
  readonly level: number;
}

export interface BuildingStats {
  readonly id: string;
  readonly name: string;
  readonly function: string;
  readonly perLevelEffect: string;
  readonly maxLevel: number;
  readonly baseCost: Resources;
  readonly baseTimeSeconds: number;
  readonly prerequisites: readonly Prerequisite[];
  readonly unlockPhase: string;
  /** Set on the four fields; null for everything else. */
  readonly produces: ResourceKind | null;
}

export interface LevelCost {
  readonly level: number;
  readonly cost: Resources;
  readonly total: number;
  readonly timeSeconds: number;
}

export interface StorageRow {
  readonly level: number;
  readonly capacity: number;
  readonly protected: number;
  readonly maxLootable: number;
}

export const BUILDINGS = buildingsData.buildings as readonly BuildingStats[];
export const FIELD_PRODUCTION = fieldsData.byLevel as readonly { level: number; perHour: number }[];
export const STORAGE = storageData.byLevel as readonly StorageRow[];
export const COST_CURVE = costCurveData.byBuilding as Record<string, readonly LevelCost[]>;

/** Village flat production per resource per hour, before any fields (02 Assumptions). */
export const FLAT_PRODUCTION_PER_HOUR: number = economyData.villageFlatProductionPerHour;
/** How many fields of each resource a village has. */
export const FIELDS_PER_RESOURCE: number = economyData.fieldsPerResource;
/** Production, build time and travel all scale with this (1000 = normal speed). */
export const SERVER_SPEED_PERMILLE: number = economyData.serverSpeedPermille;
export const PROTECTED_SHARE_PERMILLE: number = economyData.protectedSharePermille;
export const MAX_LOOTABLE_SHARE_PERMILLE: number = economyData.maxLootableSharePermille;

const buildingById = new Map(BUILDINGS.map((b) => [b.id, b]));

export function buildingStats(id: string): BuildingStats {
  const found = buildingById.get(id);
  if (!found) throw new Error(`No building "${id}" in 02_Balance.xlsx.`);
  return found;
}

export function isBuilding(id: string): boolean {
  return buildingById.has(id);
}

/** The four resource fields, in sheet order. */
export const FIELDS: readonly BuildingStats[] = BUILDINGS.filter((b) => b.produces !== null);

/**
 * What the next level costs and how long it takes.
 *
 * Read from the generated curve rather than recomputed from the growth factor:
 * the sheet rounds at every level, and a server that recomputed would disagree
 * with the client's displayed price by a resource or two — which reads as the
 * game stealing from you.
 */
export function levelCost(id: string, level: number): LevelCost {
  const curve = COST_CURVE[id];
  if (!curve) throw new Error(`No cost curve for "${id}".`);
  const row = curve[level - 1];
  if (!row || row.level !== level) {
    throw new Error(`${buildingStats(id).name} has no level ${level} (max ${curve.length}).`);
  }
  return row;
}

/** Production per hour of ONE field at this level. Level 0 produces nothing. */
export function fieldProduction(level: number): number {
  if (level <= 0) return 0;
  const row = FIELD_PRODUCTION[Math.min(level, FIELD_PRODUCTION.length) - 1];
  return row ? row.perHour : 0;
}

/** Warehouse / Granary row for a level. Level 0 uses level 1's capacity floor. */
export function storageRow(level: number): StorageRow {
  const row = STORAGE[Math.max(1, Math.min(level, STORAGE.length)) - 1];
  if (!row) throw new Error(`No storage row for level ${level}.`);
  return row;
}

/** What a brand-new village starts with, of each resource (02 Assumptions). */
export const STARTING_RESOURCES_PER_KIND: number = economyData.startingResourcesPerKind;

/** Beginner's protection, in days (02 Assumptions; D-118). */
export const NEWBIE_PROTECTION_DAYS: number = economyData.newbieProtectionDays;
/** …extended to this many if the player has launched no attack. */
export const NEWBIE_PROTECTION_DAYS_IF_PEACEFUL: number = economyData.newbieProtectionDaysIfPeaceful;
/** Full-loot attacks one attacker may make on one target per 24 hours; later ones take nothing. */
export const FULL_LOOT_ATTACKS_PER_DAY: number = economyData.fullLootAttacksPerTargetPerDay;

/**
 * What a village has on day one.
 *
 * Read from the Buildings sheet's own "Unlock order" column rather than listed
 * here: the five rows marked "Start" are the Main Building and the four fields,
 * and if the sheet ever changes its mind about that, so does this.
 */
export function startingBuildings(): { id: string; level: number }[] {
  return BUILDINGS.filter((b) => b.unlockPhase.trim().toLowerCase() === "start").map((b) => ({
    id: b.id,
    level: 1,
  }));
}

export function emptyResources(): Resources {
  return { wood: 0, clay: 0, iron: 0, grain: 0 };
}
